import { useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select } from "@/components/base/FormControls";
import { useFormules } from "@/store/formules/FormulesProvider";
import { useBilling } from "@/store/billing/BillingProvider";
import {
  PAIEMENT_METHODE_OPTIONS,
  PAIEMENT_STATUT_OPTIONS,
  paiementReference,
  type PaiementMethode,
  type PaiementStatut,
  type PlatformPaiement,
  type PlatformPaiementInput,
} from "@/store/billing/types";

interface PaiementFormProps {
  open: boolean;
  onClose: () => void;
  paiement: PlatformPaiement | null;
  /** Abonnement pré-sélectionné (création depuis un abonnement). */
  defaultAbonnementId?: string;
}

interface FormState {
  abonnementId: string;
  montant: string;
  date: string;
  methode: PaiementMethode;
  reference: string;
  statut: PaiementStatut;
  periode: string;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthISO(): string {
  return new Date().toISOString().slice(0, 7);
}

const EMPTY_STATE: FormState = {
  abonnementId: "",
  montant: "0",
  date: "",
  methode: "orange_money",
  reference: "",
  statut: "en_attente",
  periode: "",
};

/**
 * Création / modification d'un paiement d'abonnement.
 * Le paiement est rattaché à un abonnement, donc à un compte et à une formule :
 * aucun paiement orphelin n'est possible.
 */
export default function PaiementForm({ open, onClose, paiement, defaultAbonnementId }: PaiementFormProps) {
  const { formules } = useFormules();
  const { abonnements, addPaiement, updatePaiement } = useBilling();
  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const formuleNom = (id: string) => formules.find((formule) => formule.id === id)?.nom ?? id;

  useEffect(() => {
    if (!open) return;
    if (paiement) {
      setForm({
        abonnementId: paiement.abonnementId,
        montant: String(paiement.montant),
        date: paiement.date,
        methode: paiement.methode,
        reference: paiement.reference,
        statut: paiement.statut,
        periode: paiement.periode,
      });
    } else {
      setForm({ ...EMPTY_STATE, abonnementId: defaultAbonnementId ?? "", date: todayISO(), periode: monthISO() });
    }
    setErrors({});
  }, [open, paiement, defaultAbonnementId]);

  const abonnementOptions = useMemo(
    () => [
      { value: "", label: "Sélectionnez un abonnement" },
      ...abonnements.map((abonnement) => ({
        value: abonnement.id,
        label: `${abonnement.compteNom} · ${formuleNom(abonnement.formuleId)}`,
      })),
    ],
    [abonnements, formules]
  );

  const update = (patch: Partial<FormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const handleSubmit = () => {
    const nextErrors: Record<string, string> = {};
    const abonnement = abonnements.find((item) => item.id === form.abonnementId);
    if (!abonnement) nextErrors.abonnementId = "Sélectionnez l'abonnement concerné.";
    if (!form.date) nextErrors.date = "La date est obligatoire.";
    if (Number(form.montant) < 0) nextErrors.montant = "Le montant est invalide.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !abonnement) return;

    const input: PlatformPaiementInput = {
      abonnementId: abonnement.id,
      compteId: abonnement.compteId,
      compteNom: abonnement.compteNom,
      formuleId: abonnement.formuleId,
      montant: Math.max(0, Number(form.montant) || 0),
      date: form.date,
      methode: form.methode,
      reference: form.reference.trim() || paiementReference(`tmp-${abonnement.id}`),
      statut: form.statut,
      periode: form.periode.trim() || monthISO(),
    };

    if (paiement) updatePaiement(paiement.id, input);
    else addPaiement(input);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={paiement ? "Modifier le paiement" : "Nouveau paiement"}
      description="Le paiement est rattaché à un abonnement (donc à un compte et à une formule)."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-save-line text-base" aria-hidden="true" />
            Enregistrer
          </button>
        </>
      }
    >
      {abonnements.length === 0 ? (
        <p className="rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-xs leading-relaxed text-accent-900">
          Aucun abonnement disponible. Créez d'abord un abonnement pour pouvoir enregistrer un paiement.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field
            label="Abonnement concerné"
            htmlFor="pay-abonnement"
            required
            error={errors.abonnementId}
            className="sm:col-span-2"
          >
            <Select
              id="pay-abonnement"
              value={form.abonnementId}
              onChange={(event) => update({ abonnementId: event.target.value })}
              options={abonnementOptions}
              placeholder="Sélectionnez un abonnement"
              invalid={Boolean(errors.abonnementId)}
            />
          </Field>

          <Field label="Montant (FCFA)" htmlFor="pay-montant" error={errors.montant}>
            <Input
              id="pay-montant"
              type="number"
              min={0}
              value={form.montant}
              onChange={(event) => update({ montant: event.target.value })}
              invalid={Boolean(errors.montant)}
            />
          </Field>
          <Field label="Date" htmlFor="pay-date" required error={errors.date}>
            <Input
              id="pay-date"
              type="date"
              value={form.date}
              onChange={(event) => update({ date: event.target.value })}
              invalid={Boolean(errors.date)}
            />
          </Field>

          <Field label="Méthode de paiement" htmlFor="pay-methode">
            <Select
              id="pay-methode"
              value={form.methode}
              onChange={(event) => update({ methode: event.target.value as PaiementMethode })}
              options={PAIEMENT_METHODE_OPTIONS}
            />
          </Field>
          <Field label="Statut" htmlFor="pay-statut">
            <Select
              id="pay-statut"
              value={form.statut}
              onChange={(event) => update({ statut: event.target.value as PaiementStatut })}
              options={PAIEMENT_STATUT_OPTIONS}
            />
          </Field>

          <Field label="Référence" htmlFor="pay-reference" hint="Générée automatiquement si laissée vide.">
            <Input
              id="pay-reference"
              value={form.reference}
              onChange={(event) => update({ reference: event.target.value })}
              maxLength={40}
            />
          </Field>
          <Field label="Période réglée" htmlFor="pay-periode" hint="Ex. 2026-09 (mois) ou 2026 (année).">
            <Input
              id="pay-periode"
              value={form.periode}
              onChange={(event) => update({ periode: event.target.value })}
              maxLength={20}
            />
          </Field>
        </div>
      )}
    </Modal>
  );
}